import useGroupConfig, { type ApiGroupConfig, type GroupConfigEnterprise, type GroupConfigOf, type GroupConfig } from './groupConfig';
import useInvitations, { type Invitation, type ApiInvitation } from './invitation';
import type { Member } from './member';
import usePacket, { type ApiPacket, type Packet } from './packet';
import type { Fleet } from '../../fleet/domain/fleet';

import { logTrace, SeverityLevel } from '@/common/utils/logger';
import type { Guest } from '@/group/domain/guest.ts';

export type ApiObject<T> = T & {
    id?: string;
    documentId?: string;
};
export interface ApiUser {
    id: number;
    start?: string;
    user: number;
    email: string;
    access: string; // CRUD verb
    expiration?: string;
    code: number; // Password for QRcode
    groupId: string;
    createdAt: string;
    updatedAt: string;
}
export interface User {
    id: number;
    start?: string;
    user: number;
    email: string;
    access: string; // CRUD verb
    expiration?: string;
    code: number; // Password for QRcode
    groupId: string;
    createdAt: string;
    updatedAt: string;
}

export enum EnumRelation {
    OWNERSHIP = 'ownership',
    MEMBERSHIP = 'membership',
    INHERITANCE = 'inheritance'
}
export interface RelatesTo {
    targetModel: string;
    targetId: string;
    relation: EnumRelation
}

export interface ApiGroup {
    id: number;
    documentId: string;
    name: string;
    prefix: string;
    defaultDuration?: number;
    members?: Member[];
    groupRef?: string;
    guests?: Guest[];
    groupConfigs?:  ApiGroupConfig[];
    invitations?: ApiInvitation[];
    updatedAt?: string;
    createdAt?: string;
    relatesTo?: RelatesTo[];
    packet?: ApiPacket;
    cover?: string;
    assignedFleets?: Fleet[];
}

export interface GroupBody {
    adminGroup?: any;
    name: string;
    prefix?: string;
    defaultDuration?: number;
    members?: Member[];
    groupRef?: string;
    guests?: Guest[];
    groupConfigs?: GroupConfig[];
    assignedFleets?: Fleet[];
    admins?: Member[];
    invitations?: Invitation[] |  ApiInvitation[];
    packet?: Packet;
    relatesTo?: RelatesTo[];
    cover?: string;
}

export interface Group extends GroupBody {
    id: number;
    data?: any;
    documentId: string;
    updatedAt?: string;
    createdAt?: string;
}


const groupToApiGroup = (group: Group): GroupBody => {
    return {
        cover: group.cover,
        defaultDuration: group.defaultDuration,
        groupConfigs: group.groupConfigs,
        groupRef: group.groupRef,
        guests: group.guests,
        invitations: group.invitations,
        members: group.members,
        name: group.name,
        prefix: group.prefix,
        adminGroup: undefined,
        assignedFleets: group.assignedFleets,
        // Persist relation changes (e.g. fleet assignment) on update. Left undefined by callers
        // that don't touch relations, so it is dropped from the payload and existing relations
        // are preserved.
        relatesTo: group.relatesTo,
    };
};

const parentGroupId = (group: Group): number | null => {
    // loop on group configs check for type "of" then search for target model "group" and relation "inheritance" then return targetId of it
    const groupConfigOf = group.groupConfigs?.find(config => config.groupConfig?.__component === 'user.of')?.groupConfig as GroupConfigOf;
    const isGroup = groupConfigOf?.targetModel === 'group';
    const isInheritance = groupConfigOf?.relation === 'inheritance';
    return isGroup && isInheritance ? Number(groupConfigOf.targetId) : null;
};

const adminGroup = (group: Group): Group => {

    // Can be 2 cases, either the group is in group config type "of" targetModel "group" relation "inheritance" then return the group, either the group is in group config type "enterprise" then return the superAdminGrp
    const parent = parentGroupId(group);
    if (parent) {
        return group;
    }
    return ((group.groupConfigs)?.find(config => config.groupConfig.__component === 'user.enterprise')?.groupConfig as GroupConfigEnterprise)?.superAdminsGroup as Group;
};

/*
 * A populated Strapi relation reaches us either as a plain array or still wrapped in `{ data: [...] }`,
 * depending on the endpoint. Normalise both to an array so the converters below can never crash on
 * `.filter` / `.map` — a throw here fails the whole group conversion, which takes down the group's
 * fleet and fleet-admin resolution with it.
 */
const relationArray = <T>(value: unknown): T[] => {
    if (Array.isArray(value)) return value as T[];
    const wrapped = (value as { data?: unknown })?.data;
    return Array.isArray(wrapped) ? wrapped as T[] : [];
};

const apiGroupToGroup = (apiGroup: ApiGroup): Group | null => {
    if (Array.isArray(apiGroup) && apiGroup.length == 1) {
        apiGroup = apiGroup[0];
    }
    if (apiGroup == null) {
        logTrace('apiGroupToGroup: apiGroup is null or undefined', SeverityLevel.Warning);
        return null;
    }
    const { documentId, id } = apiGroup;
    const { apiInvitationsToInvitations } = useInvitations;
    const { apiGroupConfigsMapToGroupConfigs } = useGroupConfig;
    const { apiPacketToPacket } = usePacket;

    const { name, prefix, defaultDuration, members, guests, groupConfigs, invitations, updatedAt, createdAt, relatesTo, packet, groupRef, cover } = apiGroup;

    return {
        cover,
        createdAt,
        defaultDuration,
        documentId,
        // Absent stays `undefined` (not `[]`) — callers distinguish the two, e.g. `groupConfigs?.[0]`
        // guards rely on the nullish short-circuit.
        groupConfigs: groupConfigs ? apiGroupConfigsMapToGroupConfigs(relationArray(groupConfigs)) : undefined,
        groupRef,
        guests: guests ?? [],
        id: id,
        invitations: apiInvitationsToInvitations(relationArray(invitations)),
        members: members ?? [],
        name,
        packet: packet ? apiPacketToPacket(packet) : undefined,
        prefix,
        relatesTo,
        updatedAt,
        adminGroup: undefined,
        data: undefined,
        assignedFleets: apiGroup.assignedFleets ?? [],
    };
};

const apiGroupsToGroups = (data: ApiGroup[]): Group[] => {
    if (!Array.isArray(data)) {
        return [];
    }

    return data.map((apiGroup: ApiGroup) => apiGroupToGroup(apiGroup)).filter((group): group is Group => group !== null);
};

const packetRef = (group: Group, packets: Packet[]): Group => {
    const packet = packets.find(packet => packet.documentId?.toString() === group.packet?.toString());
    if (packet) {
        group.packet = packet;
    }
    return group;
};

const superFleet = (group: Group): Fleet => {
    return (group.groupConfigs?.find(config => config.groupConfig.__component === 'user.enterprise')?.groupConfig as GroupConfigEnterprise)?.superFleet as Fleet;
};

// Fleets are linked to a group through the group's `relatesTo` field (targetModel 'fleet',
// targetId = the fleet's `documentId` — the backend keys all fleet permissions/lookups on it).
// The same mechanism holdings use for parkings. There is no dedicated backend `assignedFleets`
// field, so the assigned fleets must be derived from `relatesTo` and resolved against the loaded
// fleets list. Resolution stays tolerant of a legacy numeric targetId for not-yet-migrated groups.
/*
 * `relation` narrows the match to one relation type. Leave it out for READ paths (tab visibility,
 * display): being permissive there is deliberate, so a fleet linked by any relation still shows.
 * Pass EnumRelation.MEMBERSHIP for DESTRUCTIVE paths — that is the relation a group's own fleet is
 * created with, and it excludes other fleet-targeted relations the group may carry (a member's
 * personal fleet, the fleet-admin group's ownership link), which must never be
 * swept into a delete.
 */
export const isGroupMember = (group?: { members?: Member[] } | null, userRef?: string): boolean =>
    Boolean(userRef && group?.members?.some(member => (member.user ?? member.userInfos?.ref) === userRef));

export const fleetRelationTargetIds = (
    group?: { relatesTo?: RelatesTo[] } | null,
    relation?: EnumRelation,
): string[] =>
    (group?.relatesTo ?? [])
        .filter(rel => rel.targetModel === 'fleet' && (relation === undefined || rel.relation === relation))
        .map(rel => rel.targetId);

export const resolveAssignedFleets = (
    group: { relatesTo?: RelatesTo[] } | null | undefined,
    fleets: Fleet[],
    relation?: EnumRelation,
): Fleet[] => {
    const ids = new Set(fleetRelationTargetIds(group, relation));
    if (ids.size === 0) return [];
    return (fleets ?? []).filter(
        fleet => ids.has(String(fleet.id)) || (fleet.documentId != null && ids.has(fleet.documentId))
    );
};

const useGroupConvertor = {
    adminGroup,
    apiGroupToGroup,
    apiGroupsToGroups,
    groupToApiGroup,
    packetRef,
    parentGroupId,
    superFleet,
};

export default useGroupConvertor;
